import { ButtonContent, PendingOverlay } from "@ui/components/internal/ButtonContent";
import { tv, type VariantProps } from "@ui/lib/tv";
import { Button, type ButtonProps } from "@ui/primitives/Button";
import { composeRenderProps } from "@ui/primitives/shared";
import { cx } from "@ui/utils/cx";
import { AppleLogo, FacebookLogo, GoogleLogo } from "./social-logos";

const socialButton = tv({
  slots: {
    root: [
      "relative inline-flex cursor-pointer items-center justify-center rounded-full bg-white font-semibold text-green-950 transition duration-100 ease-linear",
      "shadow-[inset_0_0_0_1.5px_var(--color-green-950),-4px_4px_0_0_var(--color-green-950)]",
      "hover:shadow-[inset_0_0_0_1.5px_var(--color-green-950),-2px_2px_0_0_var(--color-green-950)]",
      "pressed:shadow-[inset_0_0_0_1.5px_var(--color-green-950),0_0_0_0_var(--color-green-950)]",
      "disabled:cursor-not-allowed disabled:opacity-50",
      "data-[pending]:cursor-default",
    ],
    // The logo pins to the start edge so the label stays visually centred in
    // full-width buttons (the login page renders these w-full).
    icon: "pointer-events-none absolute top-1/2 -translate-y-1/2",
  },
  defaultVariants: {
    size: "48",
  },
  variants: {
    size: {
      "40": { root: "h-10 pr-3.5 pl-9 text-base leading-5", icon: "left-2 size-6" },
      "48": { root: "h-12 pr-4 pl-10 text-lg leading-6", icon: "left-2.5 size-7" },
    },
  },
});

type SocialButtonVariants = VariantProps<typeof socialButton>;

const logos = {
  apple: AppleLogo,
  facebook: FacebookLogo,
  google: GoogleLogo,
};

export type SocialButtonProps = ButtonProps & {
  size?: SocialButtonVariants["size"];
  social: keyof typeof logos;
};

export function SocialButton({ size, social, ...props }: SocialButtonProps) {
  const styles = socialButton({ size });
  const Logo = logos[social];

  return (
    <Button
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        styles.root({ ...renderProps, className }),
      )}
    >
      {composeRenderProps(props.children, (children, { isPending }) => (
        <>
          <Logo
            aria-hidden="true"
            className={cx(styles.icon(), isPending && "invisible")}
            colorful={social === "google" || social === "facebook" || undefined}
          />
          <ButtonContent isPending={isPending}>{children}</ButtonContent>
          {/* White fill — the white spinner needs inverting to stay visible. */}
          <PendingOverlay invert isPending={isPending} />
        </>
      ))}
    </Button>
  );
}
